import React, { useState, useEffect } from 'react';
import { 
  ChartBarIcon,
  DocumentArrowDownIcon,
  CalendarDaysIcon,
  BuildingOfficeIcon,
  CurrencyRupeeIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import { Layout, PlotStatus, RevenueReport, StatusDistributionReport } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/api';

interface ReportFilters {
  layout_ids: string;
  start_date: string;
  end_date: string;
  group_by: 'day' | 'week' | 'month' | 'year';
}

const ReportsPage: React.FC = () => {
  const [layouts, setLayouts] = useState<Layout[]>([]);
  const [plotStatuses, setPlotStatuses] = useState<PlotStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  const [revenueReport, setRevenueReport] = useState<RevenueReport | null>(null);
  const [statusDistribution, setStatusDistribution] = useState<StatusDistributionReport | null>(null);
  
  const [filters, setFilters] = useState<ReportFilters>({
    layout_ids: '',
    start_date: '',
    end_date: '',
    group_by: 'month'
  });

  const { state: authState } = useAuth();

  useEffect(() => {
    loadInitialData();
    loadReports();
  }, []);

  useEffect(() => {
    loadReports();
  }, [filters]);

  const loadInitialData = async () => {
    try {
      const [layoutsRes, statusesRes] = await Promise.all([
        apiService.getLayouts({ limit: 100 }),
        apiService.getPlotStatuses()
      ]);
      
      setLayouts(layoutsRes.data.items);
      setPlotStatuses(statusesRes.data.statuses);
    } catch (err: any) {
      console.error('Failed to load initial data:', err);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [revenueRes, statusRes] = await Promise.all([
        apiService.getRevenueReport(filters),
        apiService.getStatusDistribution({ layout_ids: filters.layout_ids })
      ]);
      
      setRevenueReport(revenueRes.data);
      setStatusDistribution(statusRes.data);
    } catch (err: any) {
      setError(err.message || 'Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof ReportFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      layout_ids: '',
      start_date: '',
      end_date: '',
      group_by: 'month'
    });
  };

  const exportReport = async (format: 'csv' | 'excel') => {
    try {
      setExporting(true);
      const params = {
        report_type: 'layout',
        layout_id: filters.layout_ids || 'all',
        start_date: filters.start_date,
        end_date: filters.end_date
      };
      
      const blob = format === 'csv' 
        ? await apiService.exportToCSV(params)
        : await apiService.exportToExcel(params);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `layout-report-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert(err.message || 'Failed to export report');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(1)}Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(1)}L`;
    } else {
      return `₹${amount.toLocaleString()}`;
    }
  };

  const isAdmin = authState.user?.role === 'Admin';

  if (loading && !revenueReport) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="card p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="mt-1 sm:mt-2 text-sm sm:text-base text-gray-600">Comprehensive business insights and reports</p>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn-outline flex-1 sm:flex-initial"
          >
            <FunnelIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="text-sm sm:text-base">Filters</span>
          </button>
          <button onClick={loadReports} className="btn-outline flex-1 sm:flex-initial">
            <ArrowPathIcon className="h-4 w-4 sm:h-5 sm:w-5 mr-1 sm:mr-2" />
            <span className="text-sm sm:text-base hidden sm:inline">Refresh</span>
          </button>
          {isAdmin && (
            <div className="hidden sm:flex space-x-2">
              <button
                onClick={() => exportReport('csv')}
                disabled={exporting}
                className="btn-secondary"
              >
                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                Export CSV
              </button>
              <button
                onClick={() => exportReport('excel')}
                disabled={exporting}
                className="btn-primary"
              >
                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                Export Excel
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="card p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Report Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="form-label">Layouts</label>
              <select
                className="form-input"
                value={filters.layout_ids}
                onChange={(e) => handleFilterChange('layout_ids', e.target.value)}
              >
                <option value="">All Layouts</option>
                {layouts.map(layout => (
                  <option key={layout.id} value={layout.id}>{layout.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />
            </div>

            <div>
              <label className="form-label">Group By</label>
              <select
                className="form-input"
                value={filters.group_by}
                onChange={(e) => handleFilterChange('group_by', e.target.value as any)}
              >
                <option value="day">Daily</option>
                <option value="week">Weekly</option>
                <option value="month">Monthly</option>
                <option value="year">Yearly</option>
              </select>
            </div>
          </div>
          
          <div className="flex justify-end mt-4">
            <button onClick={clearFilters} className="btn-outline">
              Clear Filters
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="card p-6">
          <div className="text-center py-12">
            <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <p className="text-red-600 mb-4">{error}</p>
            <button onClick={loadReports} className="btn-primary">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {revenueReport && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <div className="card p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Layouts</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{revenueReport.summary.total_layouts}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-full bg-blue-500 flex-shrink-0">
                <BuildingOfficeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="card p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Transactions</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{revenueReport.summary.total_transactions}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-full bg-green-500 flex-shrink-0">
                <ChartBarIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="card p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Total Revenue</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(revenueReport.summary.total_revenue)}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-full bg-purple-500 flex-shrink-0">
                <CurrencyRupeeIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
            </div>
          </div>

          <div className="card p-4 sm:p-6">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">Avg Transaction</p>
                <p className="text-xl sm:text-2xl font-bold text-gray-900">{formatCurrency(revenueReport.summary.avg_transaction_value)}</p>
              </div>
              <div className="p-2 sm:p-3 rounded-full bg-indigo-500 flex-shrink-0">
                <CalendarDaysIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        {revenueReport && revenueReport.revenueData.length > 0 && (
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Revenue Trend</h2>
            <div className="space-y-4">
              {revenueReport.revenueData.slice(0, 6).map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{item.period}</p>
                    <p className="text-sm text-gray-600">{item.layout_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-gray-900">{formatCurrency(item.total_revenue)}</p>
                    <p className="text-sm text-gray-600">{item.transaction_count} transactions</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status Distribution */}
        {statusDistribution && (
          <div className="card p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Status Distribution</h2>
            <div className="space-y-4">
              {statusDistribution.statusDistribution.map((status: any) => (
                <div key={status.id} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div 
                      className="w-4 h-4 rounded mr-3"
                      style={{ backgroundColor: status.status_color }}
                    ></div>
                    <span className="text-sm font-medium text-gray-700">
                      {status.status_name}
                    </span>
                  </div>
                  <div className="flex items-center space-x-4">
                    <span className="text-sm text-gray-600">
                      {status.plot_count} plots ({status.percentage}%)
                    </span>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatCurrency(Number(status.total_value) || 0)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Layout Performance Table */}
      {statusDistribution && statusDistribution.layoutDistribution.length > 0 && (
        <div className="card p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4">Layout Performance</h2>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Layout
                  </th>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Open
                  </th>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Reserved
                  </th>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Sold
                  </th>
                  <th className="px-4 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Value
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {Object.entries(
                  statusDistribution.layoutDistribution.reduce((acc: any, item: any) => {
                    if (!acc[item.layout_id]) {
                      acc[item.layout_id] = {
                        name: item.layout_name,
                        statuses: {}
                      };
                    }
                    acc[item.layout_id].statuses[item.status_name] = {
                      count: item.plot_count,
                      value: Number(item.total_value) || 0
                    };
                    return acc;
                  }, {} as any)
                ).map(([layoutId, data]: [string, any]) => (
                  <tr key={layoutId} className="hover:bg-gray-50">
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-900 truncate max-w-[120px] sm:max-w-none">
                      {data.name}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-500">
                      {data.statuses.Open?.count || 0}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-500 hidden sm:table-cell">
                      {(data.statuses.Reserved?.count || 0) + (data.statuses.Hold?.count || 0)}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm text-gray-500">
                      {(data.statuses.Registered?.count || 0)}
                    </td>
                    <td className="px-4 sm:px-6 py-3 sm:py-4 text-xs sm:text-sm font-medium text-gray-900">
                      {formatCurrency(
                        Object.values(data.statuses).reduce((sum: number, status: any) => sum + (Number(status.value) || 0), 0)
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage; 